






























import { computed, defineComponent, onMounted, onUnmounted, PropType, ref, toRef, watch } from "@vue/composition-api";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import { CropConfig, DragMode, ViewMode } from "./types";

export default defineComponent({
  name: "ImageCropper",
  components: { BaseIcon, BaseButton },
  props: {
    src: { type: String, required: true },
    loading: { type: Boolean, default: false },
    cropConfig: { type: Object as PropType<Required<CropConfig>>, required: true },
    previewSelector: { type: String, default: ".image-cropper__preview" },
  },
  setup(props, { emit }) {
    const image = ref();
    const minZoomValue = ref(0.1);
    const MAX_ZOOM_VALUE = 3;
    const ZOOM_STEP = 0.01;
    const zoomValue = ref(1);
    const progressWidth = computed(
      () => ((zoomValue.value - minZoomValue.value) / (MAX_ZOOM_VALUE - minZoomValue.value)) * 100 + "%"
    );
    let cropper: Cropper;

    const onZoomEvent = (evt: CustomEvent) => {
      zoomValue.value = evt.detail.ratio;
    };
    const viewMode: ViewMode = 1;
    const dragMode: DragMode = "move";
    const cropperOptions = {
      aspectRatio: props.cropConfig.aspectRatio,
      viewMode: viewMode,
      dragMode: dragMode,
      minCropBoxHeight: props.cropConfig.cropZoneHeight,
      minCropBoxWidth: props.cropConfig.cropZoneWidth,
      cropBoxMovable: false,
      cropBoxResizable: false,
      background: false,
      guides: false,
      center: false,
      modal: true,
      scalable: false,
      preview: props.previewSelector,
      zoom: (evt: CustomEvent) => onZoomEvent(evt),
      ready: () => {
        const imageData = cropper.getImageData();
        const minDimension = Math.min(imageData.naturalWidth, imageData.naturalHeight);
        const cropZoneMinDimension = Math.min(props.cropConfig.cropZoneWidth, props.cropConfig.cropZoneHeight);
        const imageSizeCropZoneRatio = cropZoneMinDimension / minDimension;
        minZoomValue.value = +imageSizeCropZoneRatio.toFixed(2);
        cropper.zoom(0.01);
        emit("setCropBox", cropper.getCropBoxData());
      },
    };

    const onZoomInput = (evt: Event) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      zoomValue.value = Number(evt.target?.value);
      cropper.zoomTo(zoomValue.value);
    };

    const handleCropImage = () => {
      cropper
        .getCroppedCanvas({
          width: props.cropConfig.exportWidth,
          height: props.cropConfig.exportHeight,
        })
        .toBlob(blob => {
          emit("imageCropped", blob);
        }, "image/jpeg");
    };

    watch(toRef(props, "src"), () => cropper.replace(props.src), {
      flush: "post",
    });

    onMounted(() => {
      cropper = new Cropper(image.value, cropperOptions);
    });

    onUnmounted(() => {
      cropper.destroy();
    });
    return {
      image,
      progressWidth,
      minZoomValue,
      MAX_ZOOM_VALUE,
      ZOOM_STEP,
      zoomValue,
      handleCropImage,
      onZoomInput,
    };
  },
});
